import React, { useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { IdashboardDataParams } from '../../models';
import { JobStatusFilterEnum, PartStatusEnum } from '../../models-enums';
import { IinitialState } from '../../reducers';
import Icon from '../common/Icon';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { setSelectedJob, toggleSearchFacilityModal } from '../../actions/manageJobActions';
import { initialJob } from '../../reducers/initialState';
import { toastr } from 'react-redux-toastr';
import { constants } from '../../constants/constants';
import Select, { components, SingleValue } from 'react-select';

import { getIsMobile } from '../../reducers/configReducer';
import { Modal } from 'react-bootstrap';

interface Option {
    value: string;
    label: string;
    data?: any;
}

interface Iprops {
    loading: boolean;
    initialParams: IdashboardDataParams;
    params: IdashboardDataParams;
    partStatusCounts: Array<number>;
    onValueChanges: (values: { [key: string]: any }) => void;
    onClear: () => void;
    isAPIQueueEmpty: boolean;
    online: boolean;
}

const formatPartStatusValue = (number: number) =>
    number.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });

const filterStatusOptions = [
    {
        value: JobStatusFilterEnum.active.toLowerCase(),
        label: JobStatusFilterEnum.active.valueOf(),
        data: undefined
    },
    {
        value: JobStatusFilterEnum.new.toLowerCase(),
        label: JobStatusFilterEnum.new.valueOf(),
        data: undefined
    },
    {
        value: JobStatusFilterEnum.inProgress.toLowerCase(),
        label: 'In Progress',
        data: undefined
    },
    {
        value: JobStatusFilterEnum.reopened.toLowerCase(),
        label: JobStatusFilterEnum.reopened.valueOf(),
        data: undefined
    },
    {
        value: JobStatusFilterEnum.reviewPending.toLowerCase(),
        label: 'Review Pending',
        data: undefined
    },
    {
        value: JobStatusFilterEnum.completed.toLowerCase(),
        label: JobStatusFilterEnum.completed.valueOf(),
        data: undefined
    }
];
const getStatusClass = (status: string) => {
    switch (status) {
        case PartStatusEnum.Delivered.toString():
            return 'green';
        case PartStatusEnum.Shipped.toString():
            return 'yellow';
        case PartStatusEnum.Ordered.toString():
            return 'red';
        default:
            return '';
    }
};

const CustomOption = ({ children, ...props }: any) => {
    const statusClass = getStatusClass(props.data.value);

    return (
        <components.Option {...props}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span className={`part-status ${statusClass}`} style={{ marginRight: '8px' }} />
                {children}
            </div>
        </components.Option>
    );
};

const CustomSingleValue = ({ children, ...props }: any) => {
    const statusClass = getStatusClass(props.data.value);

    return (
        <components.SingleValue {...props}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span className={`part-status ${statusClass}`} style={{ marginRight: '8px' }} />
                {children}
            </div>
        </components.SingleValue>
    );
};

const DashboardSearch = (props: Iprops & WithTranslation) => {
    const {
        t,
        loading,
        initialParams,
        params,
        partStatusCounts,
        onValueChanges,
        onClear,
        isAPIQueueEmpty,
        online
    } = props;

    const dispatch = useDispatch();
    const isMobile = useSelector(getIsMobile);
    const [searchText, setSearchText] = useState<string | undefined>(params.search);
    const [filterText, setFilterText] = React.useState<string | undefined>(params.filter);
    const [filterStatus, setFilterStatus] = React.useState<Option>(
        filterStatusOptions.find(o => o.value === params.status) ||
            filterStatusOptions[0] || {
                value: JobStatusFilterEnum.active.toLowerCase(),
                label: JobStatusFilterEnum.active.valueOf(),
                data: undefined
            }
    );
    const startDate = params.startDate;
    const partStatus = params.partStatus;
    const [showFilterModal, setShowFilterModal] = React.useState(false);
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false);
    const [touchInProgress, setTouchInProgress] = React.useState(false);
    const backdropCloseTimestamp = React.useRef(0);
    const selectRef = React.useRef<any>(null);

    React.useEffect(() => setSearchText(params.search), [params.search]);
    React.useEffect(() => setFilterText(params.filter), [params.filter]);
    React.useEffect(
        () =>
            setFilterStatus(
                filterStatusOptions.find(o => o.value === params.status) ||
                    filterStatusOptions[0] || {
                        value: JobStatusFilterEnum.active.toLowerCase(),
                        label: JobStatusFilterEnum.active.valueOf(),
                        data: undefined
                    }
            ),
        [params.status]
    );

    const handleClear = () => {
        setSearchText(initialParams.search);
        setFilterText(initialParams.filter);
        onClear();
        setShowFilterModal(false);
    };

    const submitOnEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onValueChanges({ search: searchText, filter: filterText });
        }
    };

    const handleApplyFilters = () => {
        onValueChanges({ search: searchText, filter: filterText });
        setShowFilterModal(false);
    };

    const togglePartStatus = (status: PartStatusEnum) => {
        const newStatus = partStatus === status ? undefined : status;
        onValueChanges({ partStatus: newStatus });
    };

    const handleDateChange = (date: string | null) => {
        if (date) {
            onValueChanges({ startDate: date });
        }
    };

    const handleAddNewJob = () => {
        if (!online) {
            toastr.warning(
                t('toastMessage:warning'),
                t('toastMessage:addJobForNewFacilityOnlineWarning'),
                constants.toastrWarning
            );
            return;
        }

        dispatch(toggleSearchFacilityModal());
        dispatch(setSelectedJob(initialJob));
    };

    const handleFilterStatusChange = (value: SingleValue<Option>) => {
        if (!value || Array.isArray(value)) {
            return;
        }

        const option = value as Option;
        onValueChanges({ status: option.value });

        const updatedOption = filterStatusOptions.find(o => o.label === option.label);

        if (updatedOption) {
            setFilterStatus(updatedOption);
        }
    };

    // Render backdrop when menu or datepicker is open to absorb clicks
    const renderBackdrop = () => {
        // Keep backdrop mounted if touch is in progress, even if picker/menu closed
        if (!isMenuOpen && !isDatePickerOpen && !touchInProgress) return null;
        
        const handleTouchEnd = (e: React.TouchEvent) => {
            const target = e.target as HTMLElement;
            
            // Don't block if touching the datepicker, select menu, or select control
            if (target.closest('.react-datepicker') || target.closest('.react-select__menu') || target.closest('.react-select__control')) {
                setTouchInProgress(false);
                return;
            }
            
            e.stopPropagation();
            e.preventDefault();
            
            // Manually close menu/picker if open
            if (isMenuOpen) {
                backdropCloseTimestamp.current = Date.now();
                // Blur the select to close the menu
                if (selectRef.current) {
                    selectRef.current.blur();
                }
                setIsMenuOpen(false);
            }
            if (isDatePickerOpen) {
                setIsDatePickerOpen(false);
            }
            
            // Clear touch state to trigger re-render and unmount backdrop
            setTouchInProgress(false);
        };
        
        const handleClick = (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            
            // Don't block if clicking the datepicker, select menu, or select control
            if (target.closest('.react-datepicker') || target.closest('.react-select__menu') || target.closest('.react-select__control')) {
                return;
            }
            
            e.stopPropagation();
            e.preventDefault();
        };
        
        const handleTouchStart = (e: React.TouchEvent) => {
            // Only set touch in progress if picker/menu is actually open
            if (isMenuOpen || isDatePickerOpen) {
                setTouchInProgress(true);
            }
        };
        
        return (
            <div
                className="dashboard-search-backdrop"
                onClick={handleClick}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            />
        );
    };

    const handleMenuOpen = () => {
        // Prevent reopening if we just closed via backdrop click (within 300ms)
        const timeSinceBackdropClose = Date.now() - backdropCloseTimestamp.current;
        if (timeSinceBackdropClose < 300) {
            return;
        }
        setIsMenuOpen(true);
    };

    const handleMenuClose = () => {
        setIsMenuOpen(false);
    };

    const handleDatePickerOpen = () => {
        setIsDatePickerOpen(true);
    };

    const handleDatePickerClose = () => {
        // Keep backdrop active briefly to capture touchend event
        setTimeout(() => {
            setIsDatePickerOpen(false);
        }, 100);
    };

    const handleFilterClick = () => {
        setShowFilterModal(true);
    };

    const handleFilterPartStatusChange = (option: SingleValue<Option>) => {
        if (!option || Array.isArray(option)) return;
        const selectedOption = (option as unknown) as {
            value: PartStatusEnum;
            label: string;
            data: undefined;
        };

        togglePartStatus(selectedOption.value);
        setShowFilterModal(false);
    };

    if (isMobile) {
        return (
            <>
                {renderBackdrop()}
                <form className="dashboard-search mobile">
                <div className="input-with-icon search">
                    <Icon name="search" />
                    <input
                        type="text"
                        placeholder={t('common:searchAnything')}
                        value={searchText}
                        disabled={loading}
                        onChange={e => setSearchText(e.target.value)}
                        onKeyDown={e => submitOnEnterKey(e)}
                    />
                </div>

                <div className="filter-search-buttons">
                    <Button onClick={handleFilterClick} variant={isMobile ? 'outline-primary' : 'default'}>
                        <FontAwesomeIcon icon={['fas', 'filter']} size="xs" />
                        Filter Search
                    </Button>
                    <Button
                        type="button"
                        disabled={!isAPIQueueEmpty}
                        onClick={handleAddNewJob}
                        variant={isMobile ? 'outline-primary' : 'default'}
                    >
                        <FontAwesomeIcon icon={['far', 'plus']} />
                        {t('nsJob:newJob')}
                    </Button>
                </div>

                {showFilterModal && (
                    <Modal
                        show={showFilterModal}
                        onHide={() => setShowFilterModal(false)}
                        dialogClassName="dashboard-filter-modal"
                        container={document.querySelector('.app') as HTMLElement}
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>{t('Filter Search')}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <section className="filter-section">
                                <p className="filter-section-title">Location</p>
                                <div className="input-with-icon facility">
                                    <Icon name="filter" />
                                    <input
                                        type="text"
                                        placeholder={t('common:filterByFacilityOrCity')}
                                        value={filterText}
                                        disabled={loading}
                                        onChange={e => setFilterText(e.target.value)}
                                    />
                                </div>
                            </section>
                            <section className="filter-section">
                                <p className="filter-section-title">Status</p>
                                <div className="input-with-icon status">
                                    <Select
                                        ref={selectRef}
                                        classNamePrefix="react-select"
                                        className="react-select-container"
                                        options={filterStatusOptions}
                                        placeholder={t('common:status')}
                                        onChange={handleFilterStatusChange}
                                        value={filterStatus}
                                        isSearchable={false}
                                        onMenuOpen={handleMenuOpen}
                                        onMenuClose={handleMenuClose}
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                        }}
                                    />
                                </div>
                            </section>
                            <section className="filter-section">
                                <p className="filter-section-title">Start Date</p>
                                <div className="input-date">
                                    <span>{t('common:startBefore')}:</span>
                                    <DatePicker
                                        selected={new Date(startDate || '')}
                                        onChange={handleDateChange}
                                        dateFormat="dd-MMM-yy"
                                        onCalendarOpen={handleDatePickerOpen}
                                        onCalendarClose={handleDatePickerClose}
                                        onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.blur()}
                                        inputMode='none'
                                        portalId="datepicker-portal"
                                    />
                                </div>
                            </section>
                            <section className="filter-section parts">
                                <p className="filter-section-title">Parts Status</p>
                                <Select
                                    classNamePrefix="react-select"
                                    options={[
                                        {
                                            value: PartStatusEnum.Delivered.toString(),
                                            label: 'Delivered/None',
                                            data: undefined
                                        },
                                        { value: PartStatusEnum.Shipped.toString(), label: 'Shipped', data: undefined },
                                        { value: PartStatusEnum.Ordered.toString(), label: 'Ordered', data: undefined }
                                    ]}
                                    placeholder={t('common:partsStatus')}
                                    onChange={handleFilterPartStatusChange}
                                    components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
                                    value={
                                        partStatus
                                            ? {
                                                  value: partStatus.toString(),
                                                  label: PartStatusEnum[partStatus],
                                                  data: undefined
                                              }
                                            : null
                                    }
                                    isSearchable={false}
                                />
                            </section>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button onClick={() => handleClear()}>
                                {t('Clear Filters')}
                                <Icon name="x" />
                            </Button>
                            <Button onClick={handleApplyFilters} className="blue">
                                {t('Apply Filters')}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                )}
            </form>
            </>
        );
    }

    return (
        <>
            {renderBackdrop()}
            <form className="dashboard-search">
            <div className="input-with-icon search">
                <Icon name="search" />
                <input
                    type="text"
                    placeholder={t('common:searchAnything')}
                    value={searchText}
                    disabled={loading}
                    onChange={e => setSearchText(e.target.value)}
                    onKeyDown={e => submitOnEnterKey(e)}
                />
            </div>

            <div className="input-with-icon status">
                <Select
                    ref={selectRef}
                    classNamePrefix="react-select"
                    className="react-select-container"
                    options={filterStatusOptions}
                    placeholder={t('common:status')}
                    onChange={handleFilterStatusChange}
                    value={filterStatus}
                    isSearchable={false}
                    onMenuOpen={handleMenuOpen}
                    onMenuClose={handleMenuClose}
                    menuPortalTarget={document.body}
                    styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                    }}
                />
            </div>

            <div className="divider" />
            <div>
                <Button
                    type="button"
                    disabled={!isAPIQueueEmpty}
                    onClick={handleAddNewJob}
                    variant="default"
                    className="dashboard-search-add-job-button"
                >
                    <FontAwesomeIcon icon={['far', 'plus']} />
                    {t('nsJob:newJob')}
                </Button>
            </div>

            <div className="input-with-icon facility">
                <Icon name="filter" />
                <input
                    type="text"
                    placeholder={t('common:filterByFacilityOrCity')}
                    value={filterText}
                    disabled={loading}
                    onChange={e => setFilterText(e.target.value)}
                    onKeyDown={e => submitOnEnterKey(e)}
                />
            </div>

            <div className="input-date">
                <span>{t('common:startBefore')}:</span>
                <DatePicker
                    selected={new Date(startDate || '')}
                    onChange={handleDateChange}
                    dateFormat="dd-MMM-yy"
                    onCalendarOpen={handleDatePickerOpen}
                    onCalendarClose={handleDatePickerClose}
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.blur()}
                    inputMode='none'
                />
            </div>

            <div className="parts">
                <OverlayTrigger
                    container={document.querySelector('.dashboard') as HTMLElement}
                    placement="bottom"
                    trigger={['hover', 'focus']}
                    overlay={
                        <Tooltip id="parts-status-tooltip" className="custom-tooltip">
                            POD or NO PARTS
                        </Tooltip>
                    }
                >
                    <div
                        className={`green ${partStatus === PartStatusEnum.Delivered ? 'on' : ''}`}
                        onClick={() => togglePartStatus(PartStatusEnum.Delivered)}
                        aria-label="POD or NO PARTS"
                    >
                        {formatPartStatusValue(partStatusCounts[0] ?? 0)}
                    </div>
                </OverlayTrigger>
                <OverlayTrigger
                    container={document.querySelector('.dashboard') as HTMLElement}
                    placement="bottom"
                    trigger={['hover', 'focus']}
                    overlay={
                        <Tooltip id="parts-status-tooltip" className="custom-tooltip">
                            SHIPPED BUT NO POD
                        </Tooltip>
                    }
                >
                    <div
                        className={`yellow ${partStatus === PartStatusEnum.Shipped ? 'on' : ''}`}
                        onClick={() => togglePartStatus(PartStatusEnum.Shipped)}
                        aria-label="SHIPPED BUT NO POD"
                    >
                        {formatPartStatusValue(partStatusCounts[1] ?? 0)}
                    </div>
                </OverlayTrigger>
                <OverlayTrigger
                    container={document.querySelector('.dashboard') as HTMLElement}
                    placement="bottom"
                    trigger={['hover', 'focus']}
                    overlay={
                        <Tooltip id="parts-status-tooltip" className="custom-tooltip">
                            PARTS NOT SHIPPED /AVAIL
                        </Tooltip>
                    }
                >
                    <div
                        className={`red ${partStatus === PartStatusEnum.Ordered ? 'on' : ''}`}
                        onClick={() => togglePartStatus(PartStatusEnum.Ordered)}
                        aria-label="PARTS NOT SHIPPED /AVAIL"
                    >
                        {formatPartStatusValue(partStatusCounts[2] ?? 0)}
                    </div>
                </OverlayTrigger>
            </div>
            <Button 
                type="button" 
                disabled={
                    loading ||
                    (searchText === initialParams.search &&
                        filterText === initialParams.filter &&
                        startDate === initialParams.startDate &&
                        partStatus === initialParams.partStatus &&
                        params.status === initialParams.status)
                }
                onClick={handleClear} 
                variant="default"
                className={
                    searchText !== initialParams.search ||
                    filterText !== initialParams.filter ||
                    startDate !== initialParams.startDate ||
                    partStatus !== initialParams.partStatus ||
                    params.status !== initialParams.status
                        ? 'blue'
                        : ''
                }
            >
                {t('common:clear')}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M3.59946 2.34311C3.25233 1.99598 2.68951 1.99598 2.34238 2.34311C1.99525 2.69025 1.99525 3.25306 2.34238 3.60019L6.74216 7.99997L2.34238 12.3997C1.99525 12.7469 1.99525 13.3097 2.34238 13.6568C2.68951 14.004 3.25233 14.004 3.59946 13.6568L7.99924 9.25705L12.399 13.6568C12.7461 14.004 13.309 14.004 13.6561 13.6568C14.0032 13.3097 14.0032 12.7469 13.6561 12.3997L9.25631 7.99997L13.6561 3.60019C14.0032 3.25306 14.0032 2.69025 13.6561 2.34311C13.309 1.99598 12.7461 1.99598 12.399 2.34311L7.99923 6.74289L3.59946 2.34311Z"
                        fill="currentColor"
                    />
                </svg>
            </Button>
        </form>
        </>
    );
};

const mapStateToProps = (state: IinitialState, ownProps: Iprops) => {
    return {};
};

export default withTranslation('dashboard')(connect(mapStateToProps, {})(DashboardSearch));
